import React, { useMemo, useRef, useEffect } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import { setToast } from '../redux/actions/window';
import 'quill/dist/quill.snow.css';
import styled from 'styled-components';
import { Palette } from './utils/palette';
import { StyledCard, StyledCardBody } from './Cards';
import { Fonts } from './utils';
import { sanitizeUrl, validateHtmlUrls } from '../utils/urlSanitizer';
import { useDispatch } from 'react-redux';
import { css } from 'styled-components';

const AmenitiesRichTextEditor = ({ value, onChange, placeholder = "Enter description...", setValue, fieldName }) => {
  const editorRef = useRef(null);
  const dispatch = useDispatch();
  
  const validateContent = (content) => {
    try {
      validateHtmlUrls(content);
      return true;
    } catch (error) {
      dispatch(setToast({
        toastShow: true,
        toastMessage: error.message,
        modal: 'errorToast'
      }));
      return false;
    }
  };

  const handleChange = (content) => {
    if (validateContent(content)) {
      const isEmpty = content.trim() === '' || content.trim() === '<p><br></p>' || content.trim() === '<p></p>';
      const contentToSave = isEmpty ? '' : content;
      if (setValue && fieldName) {
        setValue(fieldName, contentToSave, { shouldDirty: true });
      } else {
        onChange(contentToSave);
      }
    }
  };
  
  useEffect(() => {
    if (editorRef.current && value !== undefined) {
      const quill = editorRef.current.getEditor();
      if (quill) {
        const currentContent = quill.root.innerHTML;
        const normalizedValue = value || '';
        if (currentContent !== normalizedValue) {
          const delta = quill.clipboard.convert(normalizedValue);
          quill.setContents(delta, 'silent');
          if (value && typeof value === 'string' && value.endsWith('<p><br></p>')) {
            setTimeout(() => {
              const length = quill.getLength();
              if (length && length > 0) {
                const insertIndex = length;
                quill.insertText(insertIndex, '\n', 'silent');
                quill.formatText(insertIndex, 1, {
                  bold: false,
                  italic: false,
                  underline: false,
                  link: false,
                  header: false,
                  list: false
                }, 'silent');
              }
            }, 0);
          }
        }
        
        // Add target="_blank" and rel="noopener noreferrer" to external links
        const updateExternalLinks = () => {
          const links = quill.root.querySelectorAll('a[href^="http://"], a[href^="https://"]');
          links.forEach(link => {
            link.setAttribute('target', '_blank');
            link.setAttribute('rel', 'noopener noreferrer');
          });
        };
        
        updateExternalLinks();
        
        // Set up mutation observer to handle dynamically added links
        const observer = new MutationObserver(updateExternalLinks);
        observer.observe(quill.root, { childList: true, subtree: true });
        
        return () => observer.disconnect();
      }
    }
  }, [value]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          ['bold', 'italic', 'underline'],
          [{ color: [] }, { background: [] }],
          ['link'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ],
        handlers: {
          link: function(value) {
            if (value) {
              const href = prompt('Enter the URL:');
              if (href) {
                try {
                  const sanitizedUrl = sanitizeUrl(href);
                  const range = this.quill.getSelection();
                  this.quill.formatText(range, 'link', sanitizedUrl);
                } catch (error) {
                  dispatch(setToast({
                    toastShow: true,
                    toastMessage: error.message || 'Please enter a valid URL',
                    modal: 'errorToast'
                  }));
                }
              }
            } else {
              this.quill.format('link', false);
            }
          }
        }
      }
    }),
    []
  );

  const formats = [
    'bold', 'italic', 'underline',
    'link',
    'list', 'bullet',
    'header',
    'color',
    'background',
    'font',
    'size'
  ];

  return (
    <StyledCard padding="0" boxShadow="none" cssOverride={css`overflow: visible;`}>
      <StyledCardBody>
        <StyledCustomReactQuillBody
          modules={modules}
          formats={formats}
          value={value}
          onChange={handleChange}
          ref={editorRef}
          placeholder={placeholder}
        />
      </StyledCardBody>
    </StyledCard>
  );
};

const StyledCustomReactQuillBody = styled(ReactQuill)`
  .ql-toolbar.ql-snow {
    border: 1px solid #ccc;
    border-bottom: none;
    padding: 8px;
  }

  .ql-container.ql-snow {
    border: 1px solid #ccc;
    border-top: none;
    overflow: visible;
    position: relative;
  }

  .ql-editor {
    padding: 12px;
    min-height: 100px;
  }

  .ql-snow .ql-picker-label {
    padding-left: 0;
  }

  .ql-tooltip {
    position: absolute !important;
    z-index: 99999 !important;
    top: 100% !important;
    left: 0 !important;
    margin-top: 8px !important;
    max-width: 100%;
    word-wrap: break-word;
  }

  .ql-tooltip.ql-editing {
    position: absolute !important;
    z-index: 99999 !important;
    top: 100% !important;
    left: 0 !important;
    margin-top: 8px !important;
    max-width: 100%;
    word-wrap: break-word;
  }

  .ql-snow .ql-picker.ql-expanded .ql-picker-options {
    position: absolute !important;
    z-index: 99999 !important;
    contain: none !important;
  }

  .ql-editor.ql-blank::before {
    color: ${Palette.BLACK_50};
    font-family: ${Fonts.OPEN_SANS_FONT};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    left: 12px;
  }

  p {
    color: ${Palette.BLACK} !important;
    margin: 0 0 8px 0;
  }

  a {
    color: #007bff;
    text-decoration: underline;
  }

  a:hover {
    color: #0056b3;
  }

  ul, ol {
    margin: 0 0 8px 0;
    padding-left: 20px;
  }

  h1, h2, h3 {
    margin: 0 0 8px 0;
    font-weight: bold;
  }

  h1 { font-size: 1.5em; }
  h2 { font-size: 1.3em; }
  h3 { font-size: 1.1em; }
`;

export default AmenitiesRichTextEditor;
